<template>
  <v-container fluid class="profile-container">
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card elevation="4" class="profile-card">
          <div class="profile-header pa-8 text-center">
            <v-avatar size="120" class="profile-avatar mb-4 elevation-4">
              <span class="text-h3 text-white">
                {{ userInitials }}
              </span>
            </v-avatar>
            <h2 class="text-h5 font-weight-bold mb-2">
              {{ user?.name || 'Cargando...' }}
            </h2>
            <v-chip
              :color="getRoleColor(user?.role)"
              variant="flat"
              size="large"
              class="text-capitalize px-4"
            >
              <v-icon start>{{ getRoleIcon(user?.role) }}</v-icon>
              {{ user?.role || 'N/D' }}
            </v-chip>
          </div>

          <v-card-text class="pa-8">
            <h3 class="text-subtitle-1 font-weight-medium mb-4">Datos personales</h3>
            <v-row>
              <v-col cols="12" md="6">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="28">mdi-account</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Nombre Completo
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-1 text-high-emphasis">
                    {{ user?.name || 'N/D' }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>

              <v-col cols="12" md="6">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="28">mdi-email</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Correo Electrónico
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-1 text-high-emphasis">
                    {{ user?.email || 'N/D' }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <h3 class="text-subtitle-1 font-weight-medium mb-4">Cuenta</h3>
            <v-row>
              <v-col cols="12" md="6">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="28">mdi-shield-account</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Rol
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-1 text-high-emphasis text-capitalize">
                    {{ user?.role || 'N/D' }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>

              <v-col cols="12" md="6" v-if="user?.role === 'student'">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="28">mdi-account-group</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Grupo principal
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-1 text-high-emphasis">
                    {{ user?.group?.name || 'Sin grupo asignado' }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>

              <v-col cols="12" v-if="user?.role === 'student' && userGroups.length > 0">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="secondary" size="28">mdi-account-multiple</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Especialidades / Grupos
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      v-for="g in userGroups"
                      :key="g.id"
                      size="small"
                      :color="g.type === 'specialty' ? 'secondary' : 'grey'"
                      :variant="g.type === 'specialty' ? 'tonal' : 'outlined'"
                      class="mr-1 mb-1"
                    >
                      {{ g.name }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>

              <v-col cols="12" :md="user?.role === 'student' ? 12 : 6">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon :color="user?.is_active ? 'success' : 'error'" size="28">
                      {{ user?.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Estado de la cuenta
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="user?.is_active ? 'success' : 'error'"
                      size="medium"
                      variant="elevated"
                      class="px-3"
                    >
                      <v-icon start>
                        {{ user?.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                      {{ user?.is_active ? 'Activo' : 'Inactivo' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <h3 class="text-subtitle-1 font-weight-medium mb-4">Actividad</h3>
            <v-row>
              <v-col cols="12" md="6">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="28">mdi-calendar-plus</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Creado el
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-1 text-high-emphasis">
                    {{ formatDate(user?.created_at) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>

              <v-col cols="12" md="6">
                <v-list-item class="px-0">
                  <template v-slot:prepend>
                    <v-icon color="primary" size="28">mdi-update</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2 text-medium-emphasis mb-1">
                    Última actualización
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-1 text-high-emphasis">
                    {{ formatDate(user?.updated_at) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mt-4"
          dismissible
          @click:close="clearError"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

export default {
  name: 'ProfileView',
  setup() {
    const router = useRouter()
    const store = useStore()

    const user = computed(() =>
      store.getters['auth/userName']
        ? {
            name: store.getters['auth/userName'],
            email: store.getters['auth/userEmail'],
            role: store.getters['auth/userRole'],
            is_active: store.getters['auth/userIsActive'],
            created_at: store.getters['auth/userCreatedAt'],
            updated_at: store.getters['auth/userUpdatedAt'],
            group: store.getters['auth/userGroup'],
            group_id: store.getters['auth/userGroupId'],
            groups: store.getters['auth/userGroups'],
          }
        : null,
    )

    const userGroups = computed(() => {
      if (!user.value?.groups) return []
      return user.value.groups.filter(g => g.type !== 'primary')
    })

    const loading = computed(() => store.getters['auth/loading'])
    const error = computed(() => store.getters['auth/error'])

    const userInitials = computed(() => {
      if (!user.value?.name) return 'U'
      const names = user.value.name.split(' ')
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase()
      }
      return names[0][0].toUpperCase()
    })

    const getRoleColor = (role) => {
      switch (role) {
        case 'admin':
          return '#e57373'
        case 'teacher':
          return '#fbc02d'
        case 'student':
          return '#1976d2'
        default:
          return '#90a4ae'
      }
    }

    const getRoleIcon = (role) => {
      switch (role) {
        case 'admin':
          return 'mdi-crown'
        case 'teacher':
          return 'mdi-school'
        case 'student':
          return 'mdi-account-school'
        default:
          return 'mdi-account'
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/D'
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }

    const clearError = () => {
      store.dispatch('auth/clearError')
    }

    const refreshProfile = async () => {
      await store.dispatch('auth/fetchProfile')
    }

    const goToDashboard = () => {
      const dashboardRoute = store.getters['auth/getDashboardRoute']
      router.push(dashboardRoute)
    }

    const handleLogout = async () => {
      await store.dispatch('auth/logout')
      router.push('/login')
    }

    onMounted(() => {
      if (store.getters['auth/isLoggedIn']) {
        store.dispatch('auth/fetchProfile')
      } else {
        router.push('/login')
      }
    })

    return {
      user,
      userGroups,
      loading,
      error,
      userInitials,
      getRoleColor,
      getRoleIcon,
      formatDate,
      clearError,
      refreshProfile,
      goToDashboard,
      handleLogout,
    }
  },
}
</script>

<style scoped>
.profile-container {
  background: #f8f9fa;
  min-height: 100vh;
  padding-top: 4rem;
  padding-bottom: 2rem;
}

.profile-card {
  border-radius: 16px;
  overflow: hidden;
}

.profile-header {
  background: linear-gradient(135deg, #092846, #185b92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px 16px 0 0;
  color: white;
}

.profile-avatar {
  border: 4px solid white;
}
</style>
